import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Eye, EyeOff, Mail, Lock } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import type { LoginFormValues } from "../../validations/login-form"
import { loginSchema } from "../../validations/login-form"
import useLogin from "../../queries/use-login"

export default function LoginForm() {
  const loginQuery = useLogin()
  const [showPassword, setShowPassword] = useState(false)

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const handleOnSubmit = async (data: LoginFormValues) => {
    try {
      await loginQuery.mutateAsync(data)
    } catch {
      form.setError("email", { message: "Credenciales inválidas" })
    }
  }

  return (
    <div className="mx-auto w-full max-w-md space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold tracking-tight">Iniciar sesión</h2>
        <p className="text-sm text-muted-foreground">Ingresa tus credenciales para acceder</p>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleOnSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                    <Input
                      placeholder="Correo electrónico"
                      className="pl-10"
                      {...field}
                      disabled={loginQuery.isPending}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Contraseña"
                      className="pl-10"
                      disabled={loginQuery.isPending}
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-end">
            <Button
              variant="link"
              className="px-0 font-normal"
              type="button"
              disabled={loginQuery.isPending}
            >
              ¿Olvidaste tu contraseña?
            </Button>
          </div>
          <Button type="submit" className="w-full" disabled={loginQuery.isPending}>
            Iniciar sesión
          </Button>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              ¿No tienes una cuenta?{" "}
              <Button variant="link" className="px-0 font-normal" disabled={loginQuery.isPending}>
                Regístrate aquí
              </Button>
            </p>
          </div>
        </form>
      </Form>
    </div>
  )
}
